import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";
import { faPlus, faTableColumns, faTableRows } from "@fortawesome/pro-solid-svg-icons";

import { usePageTitle } from "@/utils/usePageTitle";
import ToDoIcon from "@/assets/images/to-do/to-do.png";
import InProgressIcon from "@/assets/images/to-do/in-progress.png";
import DoneIcon from "@/assets/images/to-do/done.png";
import { Button } from "@/components/Button/Button";
import { Icon } from "@/components/Icon/Icon";

import {
	subTasksSelector,
	tasksSelector,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.slice";

import {
	useGetAllSubTasksQuery,
	useGetAllTasksByUserIdQuery,
	useGetAllTasksQuery,
	useGetAllUnassignedTasksAndSubtasksQuery,
} from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

import { useGetCommentsAllQuery } from "@/pages/Private/redux/tasks-and-subtasks/task-and-subtasks-activity.api";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { Toggle } from "@/components/Toggle/Toggle";

import { useSearch } from "@/utils/useSearch";

import ToDoContainer from "./components/ToDoContainer";
import ToDoDetails from "./components/ToDoDetails";
import { ToDoStatus } from "./enum/ToDoStatus.enum";
import ToDoFilterPanel from "./components/ToDoFilterPanel";
import CreateNewTaskModal from "../../TasksAndSubTasks/components/CreateNewTaskModal";
import { SubTaskInfo, TaskInfo } from "../../TasksAndSubTasks/schema/tasks-and-subtasks";
import SubTaskDetails from "../../TasksAndSubTasks/components/SubTaskDetails";
import ToDoSwimlaneList from "../../ToDo2/ToDoSwimlaneList";
import { useSelectedParam } from "../../TasksAndSubTasks/useSelectedParam";

export default function ToDoList() {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	const { query: debouncedSearch } = useSearch();

	const { isLoading } = useGetAllTasksQuery(
		debouncedSearch ? { search: debouncedSearch } : undefined
	);
	const { isLoading: isLoadingSubTask } = useGetAllSubTasksQuery(
		debouncedSearch ? { search: debouncedSearch } : undefined
	);
	const [filterTagId, setFilterTagId] = useState<number>(-1);
	const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);

	const [selectedUserId, setSelectedUserId] = useState<number | "unassigned" | null>(null);

	const { parsed, openSelected, closeSelected } = useSelectedParam();

	const activeTaskId = parsed?.subtaskId ? null : parsed?.taskId || null;
	const selectedSubTaskId = parsed?.subtaskId ? parsed?.subtaskId || null : null;

	const isUserFilter = typeof selectedUserId === "number";
	const isUnassignedFilter = selectedUserId === "unassigned";

	const { data: userTasksAndSubTasks, isLoading: isLoadingUserTasks } = useGetAllTasksByUserIdQuery(
		selectedUserId as number,
		{
			skip: !isUserFilter,
		}
	);

	const { data: unassignedTasksAndSubTasks, isLoading: isLoadingUnassigned } =
		useGetAllUnassignedTasksAndSubtasksQuery(undefined, {
			skip: !isUnassignedFilter,
		});

	const tasks = useSelector(tasksSelector);
	const subtasks = useSelector(subTasksSelector);

	usePageTitle(ts("title"));

	const { user } = useAppSelector(authSelector);
	const companyId = user?.company?.id;
	const taskIds = useMemo(() => tasks.map((t) => t.id).filter(Boolean) as number[], [tasks]);
	const subtaskIds = useMemo(
		() => subtasks.map((s) => s.id).filter(Boolean) as number[],
		[subtasks]
	);

	const isDataReady = !isLoading && !isLoadingSubTask;

	const [searchValue, setSearchValue] = useState("");

	useGetCommentsAllQuery(
		{ taskIds, subtaskIds, companyId: companyId! },
		{
			skip: !isDataReady || (taskIds.length === 0 && subtaskIds.length === 0) || !companyId,
		}
	);
	const { task, inProgress, done, qa, searchFiltered } = useMemo(() => {
		let baseTasks: TaskInfo[];
		let baseSubTasks: SubTaskInfo[];

		if (isUserFilter && userTasksAndSubTasks) {
			baseTasks = userTasksAndSubTasks.filter((i) => i.isTask) as TaskInfo[];
			baseSubTasks = userTasksAndSubTasks.filter((i) => !i.isTask) as SubTaskInfo[];
		} else if (isUnassignedFilter && unassignedTasksAndSubTasks) {
			baseTasks = unassignedTasksAndSubTasks.filter((i) => i.isTask) as TaskInfo[];
			baseSubTasks = unassignedTasksAndSubTasks.filter((i) => !i.isTask) as SubTaskInfo[];
		} else {
			baseTasks = tasks;
			baseSubTasks = subtasks;
		}

		const tasksAndSubTasks = [...baseTasks, ...baseSubTasks];

		const filteredTaskData =
			filterTagId !== -1
				? tasksAndSubTasks.filter((item) => item?.tagId === filterTagId)
				: tasksAndSubTasks;

		const searchFiltered = searchValue.trim()
			? filteredTaskData.filter((item) => {
					const lower = searchValue.toLowerCase();

					return (
						item.title?.toLowerCase().includes(lower) ||
						item.description?.toLowerCase().includes(lower)
					);
			  })
			: filteredTaskData;

		const groupByParent = (items: typeof searchFiltered) => {
			const parentTasks = items.filter((item) => item.isTask) as TaskInfo[];
			const subTaskItems = items.filter((item) => !item.isTask) as SubTaskInfo[];

			const attachedSubTaskIds = new Set<number>();

			const grouped = parentTasks.flatMap((parent) => {
				const children = subTaskItems.filter((sub) => sub.taskId === parent.id);

				children.forEach((c) => attachedSubTaskIds.add(c.id!));

				return [parent, ...children];
			});

			const orphans = subTaskItems.filter((sub) => !attachedSubTaskIds.has(sub.id!));

			return [...grouped, ...orphans];
		};
		const task = groupByParent(
			searchFiltered.filter((item) => item.status === ToDoStatus.TO_DO && item?.id !== undefined)
		);

		const inProgress = groupByParent(
			searchFiltered.filter(
				(item) => item.status === ToDoStatus.IN_PROGRESS && item?.id !== undefined
			)
		);
		const qa = groupByParent(
			searchFiltered.filter((item) => item.status === ToDoStatus.QA && item?.id !== undefined)
		);
		const done = groupByParent(
			searchFiltered.filter((item) => item.status === ToDoStatus.DONE && item?.id !== undefined)
		);

		return { task, inProgress, done, searchFiltered, qa };
	}, [
		tasks,
		subtasks,
		filterTagId,
		selectedUserId,
		userTasksAndSubTasks,
		unassignedTasksAndSubTasks,
		searchValue,
	]);

	const filterName = useMemo(() => {
		if (filterTagId === -1) {
			return ts("all");
		}

		return tasks.find((item) => item?.tagId === filterTagId)?.tag?.name ?? "";
	}, [filterTagId, tasks, subtasks]);

	const overallProgress = useMemo(() => {
		if (!done?.length || !searchFiltered?.length) {
			return 0;
		}

		return (done.length / searchFiltered.length) * 100;
	}, [searchFiltered, done]);

	const selectedSubTask = subtasks?.find((s) => s.id === selectedSubTaskId) ?? null;

	const handleShowTaskDetails = (taskId: number) => {
		const task = tasks.find((t) => t.id === taskId);

		openSelected(taskId, undefined, task?.tagId ?? 0);
	};

	const handleShowSubTaskDetails = (subTaskId: number) => {
		const sub = subtasks.find((s) => s.id === subTaskId);
		const parent = tasks.find((t) => t.id === sub?.taskId);

		openSelected(sub?.taskId ?? 0, subTaskId, parent?.tagId ?? 0);
	};
	const parentTask = useMemo(
		() => (selectedSubTask ? tasks.find((t) => t.id === selectedSubTask.taskId) ?? null : null),
		[selectedSubTask, tasks]
	);
	const [viewMode, setViewMode] = useState<"column" | "swimlane">("column");
	const allItems = useMemo(() => [...tasks, ...subtasks], [tasks, subtasks]);

	return (
		<DndProvider backend={HTML5Backend}>
			<div className="min-h-screen p-6 space-y-6">
				<div className="w-full flex flex-col gap-2">
					<div className="w-full flex flex-row justify-between items-center">
						<div className="h-fit w-fit flex items-center gap-2">
							<Icon className="w-4 h-4 text-primary-blue-dark" icon={faTableColumns} />
							<Toggle
								isChecked={viewMode === "swimlane"}
								onChange={() => setViewMode(viewMode === "column" ? "swimlane" : "column")}
							/>
							<Icon className="w-4 h-4 text-primary-blue-dark" icon={faTableRows} />
						</div>
						<Button
							className="max-w-fit self-end"
							image={<Icon className="text-white w-4 h-4 mr-1" icon={faPlus} />}
							title={t("createTask.mainTitle")}
							onClick={() => setIsCreateTaskModalOpen(!isCreateTaskModalOpen)}
						/>
					</div>
					<div className="w-full flex justify-between items-center">
						<p className="text-sm font-bold text-primary-blue-dark">
							{ts("overall-progress")} {filterTagId > -1 ? `(${filterName})` : ""}
						</p>
						<p className="text-sm font-normal text-primary-blue-dark">
							{done?.length} / {searchFiltered?.length} {ts("tasks-completed")}
						</p>
					</div>
					<div className="w-full h-[10px] bg-primary-gray-lightest rounded-full">
						<div
							className="h-full bg-progress-bar rounded-full"
							style={{ width: `${overallProgress}%` }}
						/>
					</div>
					<div className="w-full flex justify-between items-center">
						<p className="text-sm font-bold text-primary-blue-dark"></p>
						<p className="text-sm font-normal text-primary-blue-dark">
							{Math.round(overallProgress)}%
						</p>
					</div>
				</div>

				<ToDoFilterPanel
					activeAssigneeId={selectedUserId}
					activeTabTagId={filterTagId}
					data={searchFiltered}
					viewMode={viewMode}
					onAssigneeFilter={setSelectedUserId}
					onFilterClick={setFilterTagId}
					onSearch={(val) => setSearchValue(val)}
				/>

				{viewMode === "column" ? (
					<div className="grid grid-cols-4 gap-6">
						<ToDoContainer
							allItems={allItems}
							data={task}
							icon={ToDoIcon}
							isLoading={isLoading || isLoadingUserTasks || isLoadingUnassigned || isLoadingSubTask}
							showSubTaskDetails={(subTaskId) => {
								handleShowSubTaskDetails(subTaskId);
							}}
							showTaskDetails={(taskId) => {
								handleShowTaskDetails(taskId);
							}}
							status={ToDoStatus.TO_DO}
						/>
						<ToDoContainer
							allItems={allItems}
							data={inProgress}
							icon={InProgressIcon}
							isLoading={isLoading || isLoadingUserTasks || isLoadingUnassigned || isLoadingSubTask}
							showSubTaskDetails={(subTaskId) => {
								handleShowSubTaskDetails(subTaskId);
							}}
							showTaskDetails={(taskId) => {
								handleShowTaskDetails(taskId);
							}}
							status={ToDoStatus.IN_PROGRESS}
						/>
						<ToDoContainer
							allItems={allItems}
							data={qa}
							icon={InProgressIcon}
							isLoading={isLoading || isLoadingUserTasks || isLoadingUnassigned || isLoadingSubTask}
							showSubTaskDetails={(subTaskId) => {
								handleShowSubTaskDetails(subTaskId);
							}}
							showTaskDetails={(taskId) => {
								handleShowTaskDetails(taskId);
							}}
							status={ToDoStatus.QA}
						/>
						<ToDoContainer
							allItems={allItems}
							data={done}
							icon={DoneIcon}
							isLoading={isLoading || isLoadingUserTasks || isLoadingUnassigned || isLoadingSubTask}
							showSubTaskDetails={(subTaskId) => {
								handleShowSubTaskDetails(subTaskId);
							}}
							showTaskDetails={(taskId) => {
								handleShowTaskDetails(taskId);
							}}
							status={ToDoStatus.DONE}
						/>
					</div>
				) : (
					<ToDoSwimlaneList
						activeTaskId={activeTaskId}
						allItems={searchFiltered}
						isCreateTaskModalOpen={isCreateTaskModalOpen}
						isLoading={isLoading || isLoadingSubTask || isLoadingUserTasks || isLoadingUnassigned}
						selectedSubTaskId={selectedSubTaskId}
						setActiveTaskId={(id) => (id ? handleShowTaskDetails(id) : closeSelected())}
						setIsCreateTaskModalOpen={setIsCreateTaskModalOpen}
						setSelectedSubTaskId={(id) => (id ? handleShowSubTaskDetails(id) : closeSelected())}
						subtasks={subtasks}
						tasks={tasks}
					/>
				)}
			</div>{" "}
			<ToDoDetails
				close={closeSelected}
				companyToDoProgressId={activeTaskId}
				showSubTaskDetails={(subTaskId) => {
					handleShowSubTaskDetails(subTaskId);
				}}
			/>
			<SubTaskDetails
				open={!!selectedSubTask}
				parentData={parentTask!}
				parentTaskId={parentTask?.id ?? -1}
				subTask={selectedSubTask}
				onClose={closeSelected}
				onOpenParentTask={(taskId) => {
					handleShowTaskDetails(taskId);
				}}
			/>
			<CreateNewTaskModal isOpen={isCreateTaskModalOpen} setIsOpen={setIsCreateTaskModalOpen} />
		</DndProvider>
	);
}
